import { ArrowDropDown, Circle } from '@mui/icons-material';
import { Box, styled, Typography, Skeleton, useTheme, useMediaQuery, Chip } from '@mui/material';
import { Stack } from '@mui/system';
import { startOfMonth, endOfMonth, getDate } from 'date-fns';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { FormattedDate, FormattedMessage } from 'react-intl';
import type { CalendarMonthDto, EventDto } from 'api/generated';
import { EventsListDialog } from 'components/common/dialogs/events-list-dialog';
import { GetColorByAvailability, HighestAvailabilityEvent } from 'lib/helpers/availability-color';
import { groupEventsByServerDay } from 'lib/helpers/calendar-helpers';
import { clientTimezone, dateKey, getDateParts } from 'lib/helpers/date-helpers';
import type { useCalendar } from 'lib/hooks/use-calendar';
import { useServerTimezone } from 'lib/hooks/use-server-timezone';
import { CalendarEventButton } from './calendar-event-button';
import { CalendarLegend } from './calendar-legend';
const DayCell = styled('div', {
  shouldForwardProp: prop => !['isCurrentMonth', 'isPast', 'hasEvents', 'isMobile'].includes(prop as string)
})<{
  isCurrentMonth: boolean;
  isPast: boolean;
  hasEvents: boolean;
  isMobile: boolean;
}>`
  min-height: 146px;
  border-radius: ${props => props.theme.shape.borderRadius}px;
  border: 1px solid ${props => props.theme.palette.divider};
  padding: 16px;
  color: ${props => props.theme.palette.text.primary};
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  background: inherit;

  ${props => props.hasEvents && props.isCurrentMonth && !props.isPast && `
    &:hover {
      padding: 15px;
      border: 2px solid ${props.theme.palette.primary.main};
    }
    
    ${props.isMobile && `
      cursor: pointer;
      &:hover {
        padding: 0;
        border: 2px solid ${props.theme.palette.primary.main};
      }`}
    
  `}

  ${props => !props.isCurrentMonth && `
    color: transparent;
    border: 0;
  `}

  ${props => !props.hasEvents && props.isCurrentMonth && (props.isMobile ? `
    border: 0;
    background-color: transparent;
    color: ${props.theme.palette.text.disabled};
  ` : `
    border: 0;
    background-color: ${props.theme.palette.background.default};
    color: ${props.theme.palette.text.disabled};
  `)}

  ${props => props.isPast && props.isCurrentMonth && `
    border: 0;
    background-color: ${props.theme.palette.action.hover};
    color: ${props.theme.palette.text.disabled};
  `}

  ${props => props.isMobile && `
      justify-content: center;
      box-sizing: border-box;
      padding: 0;
      aspect-ratio: 1 / 1;
      height: auto;
      min-height: auto;
      border-radius: ${props.theme.buttonRadius.main}px;
      border: 2px solid transparent;

      > * { 
        height: auto;
      }
    `}
`;
const Cells = styled(Box, {
  shouldForwardProp: prop => prop !== 'isMobile'
})<{
  isMobile: boolean;
}>`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 12px;
  overflow: hidden;

  ${props => props.isMobile && `gap: 4px;`}
`;
const Weekdays = styled(Box, {
  shouldForwardProp: prop => prop !== 'isMobile'
})<{
  isMobile: boolean;
}>`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 12px;

  ${props => props.isMobile && `gap: 4px;`}
`;
const Table = styled(Box)``;
const MobileEventsIndicators = styled(Box)`
  width: 100%;
  height: 5px;
  display: flex;
  justify-content: center;
  gap: 2px;
`;
export function Calendar({
  calendarInstance,
  eventGroupId,
  events,
  loading,
  onMonthChange,
  firstMonth,
  forceSmallView,
  onDateSelect,
  highlightDate
}: {
  calendarInstance: ReturnType<typeof useCalendar>;
  eventGroupId: number;
  events: EventDto[];
  loading?: boolean;
  onMonthChange?(date: Date): void;
  firstMonth?: CalendarMonthDto;
  forceSmallView?: boolean;
  onDateSelect?(day: EventDto[]): void;
  highlightDate?: Date | null;
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')) || forceSmallView;
  const [modalEventId, setModalEventId] = useState<number | null>(null);
  const {
    timezone: serverTimezone
  } = useServerTimezone();
  const {
    viewing,
    calendar,
    inRange
  } = calendarInstance;
  // The grid's day cells are browser-local dates, so events must be bucketed
  // by their day-of-month in the venue timezone — not the browser's — or they
  // shift across day/month boundaries for buyers in other timezones.
  const eventsByDate = useMemo(() => groupEventsByServerDay(events, viewing.getFullYear(), viewing.getMonth() + 1, serverTimezone), [events, viewing, serverTimezone]);

  // Calendar days as comparable keys: grid cells are local-constructed dates
  // (read with local getters), event instants are evaluated in the venue tz.
  const localDayKey = (day: Date) => dateKey({
    year: day.getFullYear(),
    month: day.getMonth() + 1,
    day: day.getDate()
  });
  const todayKey = dateKey(getDateParts(new Date(), serverTimezone));
  const highlightKey = highlightDate ? dateKey(getDateParts(highlightDate, serverTimezone)) : todayKey;
  const firstEventKey = events.length > 0 ? dateKey(getDateParts(events[0]!.startDate ?? 0, serverTimezone)) : null;
  useEffect(() => {
    onMonthChange?.(viewing);
  }, [onMonthChange, viewing]);
  return <>
      <Table sx={[forceSmallView ? {
      paddingBottom: 2
    } : {
      paddingBottom: 4
    }]} width={'100%'}>
        <Weekdays isMobile={!!isMobile}>
          {calendar[0]![0]!.map(day => <Typography variant="body3" align="center" key={`${day}`} sx={{
          color: 'text.secondary',
          p: '.5rem .5rem 1rem'
        }}>
              <FormattedDate value={day} weekday={isMobile ? 'short' : 'long'} timeZone={clientTimezone} />
            </Typography>)}
        </Weekdays>
        <Cells isMobile={!!isMobile}>
          {calendar[0]?.map((week, i) => {
          // Hide weeks before first event
          const isFirstMonth = firstMonth && week[week.length - 1]!.getFullYear() === firstMonth.year && week[week.length - 1]!.getMonth() + 1 === firstMonth.month;
          if (isFirstMonth) {
            const isPastFirstEvent = firstEventKey !== null && localDayKey(week[week.length - 1]!) < firstEventKey;
            if (isPastFirstEvent) return null;
          }
          return <Fragment key={i}>
                {week.map((day, j) => {
              const isInRange = inRange(day, startOfMonth(viewing), endOfMonth(viewing));
              // const isPastToday = isBefore(day, endOfYesterday());
              const dayEvents = eventsByDate[getDate(day)] || [];
              const dayEventsAvailable = dayEvents.filter(event => event.saleStatus !== 'Cancelled' && event.saleStatus !== 'SoldOut');
              const hasEvents = dayEventsAvailable?.length > 0;
              const hasMultiEvents = dayEvents?.length > 2;
              const highestAvailabilityEvent = HighestAvailabilityEvent(dayEvents);
              const disabled = highestAvailabilityEvent?.saleStatus === 'Cancelled' || highestAvailabilityEvent?.saleStatus === 'SoldOut';
              return <DayCell key={j} isCurrentMonth={isInRange} {...hasMultiEvents ? {
                as: 'button' as const
              } : {}} isPast={false} data-inview={isInRange ? 'true' : 'false'} hasEvents={hasEvents && !disabled} onClick={isMobile && hasEvents ? onDateSelect ? () => onDateSelect(dayEventsAvailable) : () => setModalEventId(getDate(day)) : !isMobile && hasMultiEvents ? () => setModalEventId(getDate(day)) : undefined} tabIndex={isMobile && hasEvents ? 0 : undefined} isMobile={!!isMobile} sx={[{
                border: localDayKey(day) === highlightKey ? `2px solid ${theme.palette.primary.main}` : 'inherit',
                position: 'relative'
              }, hasMultiEvents ? {
                cursor: 'pointer'
              } : {
                cursor: null
              }]}>
                      <Typography variant="body2Medium" sx={[{
                  display: 'flex'
                }, isMobile ? {
                  flex: 0
                } : {
                  flex: 1
                }, isMobile ? {
                  justifyContent: 'center'
                } : {
                  justifyContent: 'flex-start'
                }, isMobile ? {
                  alignItems: 'center'
                } : {
                  alignItems: 'flex-start'
                }]}>
                        <FormattedDate value={day} day="numeric" timeZone={clientTimezone} />
                        {localDayKey(day) === todayKey && !isMobile && <span>
                            ・
                            <FormattedMessage id="event.today" defaultMessage={[{
                      "type": 0,
                      "value": "Today"
                    }]} />
                          </span>}
                      </Typography>
                      {loading ? isInRange && Array.from({
                  length: isMobile ? 1 : 2
                }).map((_, i) => <Skeleton key={i} height={isMobile ? 5 : 30} width={isMobile ? 5 : 'auto'} variant="rectangular" animation="pulse" sx={[{
                  borderRadius: 5.5,
                  bgcolor: 'action.disabledBackground'
                }, isMobile ? {
                  marginTop: 0
                } : {
                  marginTop: 1
                }, isMobile ? {
                  mx: 'auto'
                } : {
                  mx: 0.5
                }]} />) : isInRange && (isMobile ? <MobileEventsIndicators>
                              {dayEvents.length > 0 && <Circle sx={{
                    width: '5px',
                    height: '5px',
                    color: GetColorByAvailability(highestAvailabilityEvent)
                  }} />}
                            </MobileEventsIndicators> : (eventsByDate[getDate(day)]?.length || 0) > 2 ? <>
                              {(highestAvailabilityEvent?.saleStatus === 'FewLeft' || highestAvailabilityEvent?.saleStatus === 'Cancelled' || highestAvailabilityEvent?.saleStatus === 'SoldOut') && <Chip label={highestAvailabilityEvent.saleStatus === 'FewLeft' ? <FormattedMessage id="common.fewLeft" defaultMessage={[{
                    "type": 0,
                    "value": "Few left"
                  }]} /> : highestAvailabilityEvent.saleStatus === 'SoldOut' ? <FormattedMessage id="common.soldOut" defaultMessage={[{
                    "type": 0,
                    "value": "Sold out"
                  }]} /> : <FormattedMessage id="common.cancelled" defaultMessage={[{
                    "type": 0,
                    "value": "Cancelled"
                  }]} />} size="small" color={GetColorByAvailability(highestAvailabilityEvent).split('.')[0] as 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'} sx={{
                    position: 'absolute',
                    top: '.75rem',
                    right: '.75rem'
                  }} />}
                              {highestAvailabilityEvent?.saleStatus === 'Cancelled' || highestAvailabilityEvent?.saleStatus === 'SoldOut' ? <Typography align="left" sx={{
                    color: 'text.disabled'
                  }}>
                                  {highestAvailabilityEvent.saleStatus === 'SoldOut' ? <FormattedMessage id="common.soldOut" defaultMessage={[{
                      "type": 0,
                      "value": "Sold out"
                    }]} /> : <FormattedMessage id="common.cancelled" defaultMessage={[{
                      "type": 0,
                      "value": "Cancelled"
                    }]} />}
                                </Typography> : <Stack sx={{
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                                  <FormattedMessage id="event.xEvents" defaultMessage={[{
                      "type": 6,
                      "value": "count",
                      "options": {
                        "=0": {
                          "value": [{
                            "type": 0,
                            "value": "0 events"
                          }]
                        },
                        "=1": {
                          "value": [{
                            "type": 0,
                            "value": "1 event"
                          }]
                        },
                        "other": {
                          "value": [{
                            "type": 7
                          }, {
                            "type": 0,
                            "value": " events"
                          }]
                        }
                      },
                      "offset": 0,
                      "pluralType": "cardinal"
                    }]} values={{
                      count: dayEvents.length
                    }} />
                                  <ArrowDropDown />
                                </Stack>}
                            </> : dayEvents.map(event => <CalendarEventButton key={event.id} event={event} href={`/${eventGroupId}/event/${event.id}`} />))}
                    </DayCell>;
            })}
              </Fragment>;
        })}
        </Cells>
      </Table>
      <CalendarLegend forceSmallView={forceSmallView} />
      <EventsListDialog open={!!modalEventId} onClose={() => setModalEventId(null)} eventGroupId={eventGroupId} events={modalEventId ? eventsByDate[modalEventId] ?? [] : []} />
    </>;
}